import { useOperationalMetrics } from '@/hooks/use-provider';
import { Terms, useTerms } from '@/hooks/use-terms';
import { useFinancialSummary } from '@/hooks/use-financial-summary';
import { usePaymentAccount } from '@/hooks/use-payment-account';
import { usePaymentBalance } from '@/hooks/use-payment-reporting';
import { PAYMENT_ACCOUNT_STATUS } from '@/types/payment-account';
import { formatCurrency } from '@/utils/format-currency';
import React, { useState } from 'react';
import { Platform, useWindowDimensions, View } from 'react-native';
import { router } from 'expo-router';
import { MetricCard } from './metric-card';
import { SkeletonMetric } from '../ui/skeleton';
import { ErrorState } from '../ui/error-state';
import { FadeInView } from '../ui/fade-in-view';
import { PressableScale } from '../ui/pressable-scale';
import { ThemedText } from '../themed-text';
import { cn } from '@/utils/cn';
import { HomeSummaryDialog } from './home-summary-dialog';
import { RevenueSummary } from './summaries/revenue-summary';
import { ReceivablesSummary } from './summaries/receivables-summary';
import { EnrollmentsSummary } from './summaries/enrollments-summary';
import { ClientsSummary } from './summaries/clients-summary';

const MOBILE_BREAKPOINT = 500;

const metricsGridClassName = cn(
  'w-full gap-3',
  Platform.OS === 'web' ? 'grid grid-cols-2 lg:grid-cols-4' : 'flex-row flex-wrap gap-2',
);

const metricsItemClassName = cn(Platform.OS === 'web' ? 'w-full' : 'min-w-[45%] flex-1');

type MetricKey = 'revenue' | 'receivables' | 'enrollments' | 'clients';

type SummaryConfig = Record<
  MetricKey,
  {
    icon: React.ComponentProps<typeof MetricCard>['icon'];
    title: string;
    subtitle: string;
    seeMoreLabel: string;
    route: string;
    Content: () => React.ReactElement;
  }
>;

/**
 * Cada métrica abre a prévia da página correspondente.
 *
 * Função e não constante porque metade dos rótulos é vocabulário do ramo:
 * "Contratos ativos" vira "Matrículas ativas" para quem aplicou o pack de
 * personal trainer.
 */
const summaryConfig = (terms: Terms): SummaryConfig => ({
  revenue: {
    icon: 'checkmark-circle-outline',
    title: 'Recebido',
    subtitle: 'Pagamentos confirmados no mês',
    seeMoreLabel: 'Ver relatório completo',
    route: '/(tabs)/(provider)/reports',
    Content: RevenueSummary,
  },
  receivables: {
    icon: 'time-outline',
    title: 'A receber',
    subtitle: 'Cobranças em aberto',
    seeMoreLabel: 'Ver todas as cobranças',
    route: '/(tabs)/(provider)/charges',
    Content: ReceivablesSummary,
  },
  enrollments: {
    icon: 'document-text-outline',
    title: `${terms.title.enrollmentPlural} ${terms.agree('ativos', 'enrollment')}`,
    subtitle: `${terms.title.enrollmentPlural} em andamento`,
    seeMoreLabel: `Ver ${terms.agree('todos', 'enrollment')} ${terms.agree('os', 'enrollment')} ${terms.enrollmentPlural}`,
    route: '/(tabs)/(provider)/enrollments',
    Content: EnrollmentsSummary,
  },
  clients: {
    icon: 'people-outline',
    title: terms.title.clientPlural,
    subtitle: `Sua base de ${terms.clientPlural}`,
    seeMoreLabel: `Ver ${terms.agree('todos', 'client')} ${terms.agree('os', 'client')} ${terms.clientPlural}`,
    route: '/(tabs)/(provider)/clients',
    Content: ClientsSummary,
  },
});

export function DashboardMetrics() {
  const { width } = useWindowDimensions();
  const compact = width > 0 && width < MOBILE_BREAKPOINT;
  const { data: metrics, isLoading, error, refetch } = useOperationalMetrics();
  const { data: financialSummary } = useFinancialSummary();
  const [openMetric, setOpenMetric] = useState<MetricKey | null>(null);
  const terms = useTerms();
  const summaries = summaryConfig(terms);

  // Quem recebe automático vê aqui os mesmos números de Recebimentos (saldo
  // do Stripe, líquido de taxa); a conta pelas cobranças fica para o manual.
  const { data: paymentStatus } = usePaymentAccount();
  const accountStatus = paymentStatus?.account?.status;
  const automatic =
    !!accountStatus &&
    accountStatus !== PAYMENT_ACCOUNT_STATUS.DRAFT &&
    accountStatus !== PAYMENT_ACCOUNT_STATUS.VERIFYING;
  const balance = usePaymentBalance({ enabled: automatic });

  if (isLoading || (automatic && balance.isLoading)) {
    return (
      <View className="w-full gap-3">
        <ThemedText className="font-semibold text-card-foreground">Este mês</ThemedText>
        <View className={metricsGridClassName}>
          {Array.from({ length: 4 }).map((_, i) => (
            <SkeletonMetric key={i} className={cn('h-28', metricsItemClassName)} />
          ))}
        </View>
      </View>
    );
  }

  if (error || !metrics) {
    return (
      <ErrorState
        variant="compact"
        title="Erro ao carregar métricas"
        message={error ? 'Não foi possível carregar as métricas.' : 'Dados indisponíveis.'}
        onRetry={refetch}
      />
    );
  }

  const metricCards: { key: MetricKey; value: string; label: string }[] = [
    {
      key: 'revenue',
      value: formatCurrency(
        automatic && balance.data ? balance.data.receivedThisMonth : metrics.monthlyRevenue,
      ),
      label: 'Recebido',
    },
    {
      key: 'receivables',
      value: formatCurrency(
        automatic && balance.data
          ? balance.data.pending + balance.data.inTransit
          : financialSummary?.balance.pending || 0,
      ),
      label: 'A receber',
    },
    {
      key: 'enrollments',
      value: `${metrics.activeEnrollments}`,
      label: `${terms.title.enrollmentPlural} ${terms.agree('ativos', 'enrollment')}`,
    },
    {
      key: 'clients',
      value: `${metrics.clientMetrics.total}`,
      label:
        metrics.clientMetrics.total === 1 ? terms.title.client : terms.title.clientPlural,
    },
  ];

  const active = openMetric ? summaries[openMetric] : null;

  return (
    <View className="w-full gap-3">
      <ThemedText className="font-semibold text-card-foreground">Este mês</ThemedText>
      <View className={metricsGridClassName}>
        {metricCards.map((card, i) => (
          <FadeInView key={card.key} delay={i * 70} className={metricsItemClassName}>
            <PressableScale
              haptic
              onPress={() =>
                automatic && balance.data && (card.key === 'revenue' || card.key === 'receivables')
                  ? router.push('/(tabs)/(provider)/payments' as never)
                  : setOpenMetric(card.key)
              }
              accessibilityRole="button"
              accessibilityLabel={`Ver resumo de ${card.label}`}
              className="w-full"
            >
              <MetricCard
                icon={summaries[card.key].icon}
                value={card.value}
                label={card.label}
                compact={compact}
              />
            </PressableScale>
          </FadeInView>
        ))}
      </View>

      {active && (
        <HomeSummaryDialog
          open
          onClose={() => setOpenMetric(null)}
          icon={active.icon}
          title={active.title}
          subtitle={active.subtitle}
          seeMoreLabel={active.seeMoreLabel}
          onSeeMore={() => router.push(active.route as never)}
        >
          <active.Content />
        </HomeSummaryDialog>
      )}
    </View>
  );
}
